/**
 * ElectronAPI shim for web demo.
 *
 * This module provides a complete implementation of the ElectronAPI interface
 * for browser-only usage. Methods that require Electron/Node.js functionality
 * return sensible defaults or show "Download" CTAs in later slices.
 *
 * IMPORTANT: This file must be imported before any code that accesses
 * window.electronAPI to ensure the shim is installed first.
 */

/* eslint-disable @typescript-eslint/no-unused-vars */

import type { Connection, DeleteConnectionOutcome, LanguageServerStatus } from '../types/electron';
import type {
  QueryExecutionRequest,
  QueryExecutionResult,
  QueryCancelRequest,
  QueryCancelResult,
} from '../types/queryExecution';
import type {
  GetExecutionPlanResult,
  ImportExecutionPlanResult,
  ExportExecutionPlanResult,
} from '../types/executionPlan';
import type { CollectDeadlocksResult, ImportDeadlockReportResult } from '../types/deadlockAnalyzer';
import type { QueryStoreCapabilityResult, QueryStoreCompareResult, QueryStoreDetailResult, QueryStorePlanResult } from '../types/queryStore';
import type { ExportResultsResult } from '../types/resultsExport';
import type { ChartExportResult } from '../features/workspace/charts/types/chartTypes';
import type { OpenSqlFileResult, SaveSqlFileResult } from '../types/queryFiles';
import type { OpenSqliteDatabaseFileResult } from '../types/sqlite';
import type {
  TableMetadataResult,
  DatabaseMetadataResult,
} from '../types/electron';
import type { GetTableMetadataRequest } from '../types/electron';
import type {
  TableExplorerApplyChangesRequest,
  TableExplorerFetchInboundForeignKeysRequest,
  TableExplorerFetchRowsRequest,
  TableExplorerPreviewChangesRequest,
  TableExplorerPreviewSelectRequest,
} from '../types/tableExplorer';
import type {
  TerminalCreateResult,
  TerminalWriteResult,
  TerminalResizeResult,
  TerminalCloseResult,
  TerminalRetryExportResult,
  TerminalConfirmFilterResult,
  TerminalCancelExportResult,
} from '../types/terminal';
import type { DetectedAgent } from '../types/electron';
import type { SecurityManagementAPI } from '../types/securityExplorer';
import {
  executeSqliteQuery,
  cancelSqliteQuery,
  fetchTableRows,
  getTableMetadataSqlite,
  previewSelect,
  previewChanges,
  applyChanges,
  isDatabaseOpen,
  getCurrentDatabaseId,
  closeCurrentDatabase,
  getCachedDatabase,
  loadDemoDatabase,
  openDatabaseFromBuffer,
  getSqliteDependencies,
  fetchInboundForeignKeys,
  getSqliteSchemaOverview,
} from './sqlite';

// Helper to create a noop cleanup function
const noop = () => {};

/** Dashboards RepoResult error for web mode — mutators must not throw (store awaits results). */
/** Every `window.electronAPI.azureStorage.*` call in browser mode: the typed `unsupported` StorageIpcResult. */
const azureStorageUnavailable = async (_request?: unknown) => ({
  ok: false as const,
  error: {
    category: 'unsupported' as const,
    code: 'BrowserMode',
    retryable: false,
    message: 'Azure Storage is not available in browser mode',
  },
});

const dashboardsUnavailable = () =>
  ({ ok: false, error: 'io', detail: 'Dashboards are unavailable in web mode' }) as const;

// Storage key for persisted connections in web demo
const WEB_DEMO_CONNECTIONS_KEY = 'sql-studio-web-demo-connections';

/**
 * Load connections from localStorage for web demo.
 */
const loadWebConnections = (): Connection[] => {
  try {
    const stored = localStorage.getItem(WEB_DEMO_CONNECTIONS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Save connections to localStorage for web demo.
 */
const saveWebConnections = (connections: Connection[]): void => {
  try {
    localStorage.setItem(WEB_DEMO_CONNECTIONS_KEY, JSON.stringify(connections));
  } catch {
    console.warn('[WebDemo] Failed to save connections to localStorage');
  }
};

type EnsureSqliteDatabaseResult =
  | { success: true }
  | { success: false; error: string };

let sqliteDbOpChain: Promise<void> = Promise.resolve();

function withSqliteDbLock<T>(fn: () => Promise<T>): Promise<T> {
  const run = sqliteDbOpChain.then(fn, fn);
  sqliteDbOpChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

async function ensureSqliteDatabaseOpen(connectionId: string): Promise<EnsureSqliteDatabaseResult> {
  if (isDatabaseOpen() && getCurrentDatabaseId() === connectionId) {
    return { success: true };
  }

  try {
    let buffer = await getCachedDatabase(connectionId);

    if (!buffer) {
      const { BROWSER_MODE_DATABASES } = await import('../features/browser-mode/types/browserModeTypes');
      const demoDb = BROWSER_MODE_DATABASES.find((db) => db.id === connectionId);
      if (demoDb) {
        buffer = await loadDemoDatabase(connectionId, demoDb.assetPath, () => {});
      }
    }

    if (!buffer) {
      return {
        success: false,
        error: 'Database not found in cache. Please reconnect using the connections dialog.',
      };
    }

    await openDatabaseFromBuffer(connectionId, buffer);

    // Keep the browser mode banner in sync with the active database
    const { useBrowserModeStore } = await import('../features/browser-mode/store/browserModeStore');
    const { BROWSER_MODE_DATABASES } = await import('../features/browser-mode/types/browserModeTypes');
    const demoDb = BROWSER_MODE_DATABASES.find((db) => db.id === connectionId);

    if (demoDb) {
      useBrowserModeStore.getState().setConnected(demoDb);
    } else {
      const savedConn = loadWebConnections().find((c) => c.id === connectionId);
      useBrowserModeStore.getState().setConnected({
        id: connectionId,
        name: savedConn?.alias || savedConn?.database || 'Uploaded Database',
        description: 'User-uploaded SQLite database',
        assetPath: '',
        tableCount: 0,
        sizeHint: 'Unknown',
        type: 'uploaded',
      });
    }

    return { success: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to open database';
    return { success: false, error: message };
  }
}

/**
 * Check if we're running in web demo mode.
 */
export const isWebDemo = (): boolean => {
  return typeof window !== 'undefined' && !window.electronAPI?.testMode?.isTestMode?.();
};

// Helper for creating failure results with proper typing
const failResult = <T extends { success: false; error: string }>(error: string): T =>
  ({ success: false, error }) as T;

/**
 * The complete ElectronAPI shim for web demo.
 * All methods are stubbed to prevent crashes during initial render.
 */
const electronApiShim = {
  // Message passing (no-op in web)
  sendMessage: (_channel: string, _data: unknown) => {},
  onMessage: (_channel: string, _func: (...args: unknown[]) => void) => {},

  // Connection management (uses localStorage)
  getConnections: async (): Promise<Connection[]> => {
    return loadWebConnections();
  },

  saveConnection: async (connection: Connection): Promise<boolean> => {
    const connections = loadWebConnections();
    const existingIndex = connections.findIndex((c) => c.id === connection.id);
    if (existingIndex >= 0) {
      connections[existingIndex] = connection;
    } else {
      connections.unshift(connection);
    }
    saveWebConnections(connections);
    return true;
  },

  // The post-connect `lastConnectedAt` stamp — the localStorage counterpart of
  // main's field-targeted write: one field of one record, and nothing at all
  // when the record is gone.
  touchConnectionLastConnected: async (
    connectionId: string,
    lastConnectedAt: string,
  ): Promise<boolean> => {
    const connections = loadWebConnections();
    const index = connections.findIndex((c) => c.id === connectionId);
    if (index < 0) return false;
    connections[index] = { ...connections[index], lastConnectedAt };
    saveWebConnections(connections);
    return true;
  },

  deleteConnection: async (connectionId: string): Promise<DeleteConnectionOutcome> => {
    const connections = loadWebConnections();
    const filtered = connections.filter((c) => c.id !== connectionId);
    saveWebConnections(filtered);
    return { deleted: true };
  },

  // Get decrypted password (in browser mode, passwords aren't encrypted)
  getDecryptedPassword: async (connectionId: string): Promise<string> => {
    const connections = loadWebConnections();
    const conn = connections.find((c) => c.id === connectionId);
    return conn?.password ?? '';
  },

  // Keychain APIs (not applicable in browser mode)
  keychain: {
    needsExplanation: async (): Promise<boolean> => false,
    markExplanationShown: async (): Promise<boolean> => true,
  },

  // Language server (not available in web)
  getLanguageServerStatus: async (): Promise<{
    status: LanguageServerStatus;
    details?: unknown;
  }> => ({
    status: 'closed',
    details: { reason: 'Language server not available in browser mode. Download the desktop app for IntelliSense.' },
  }),

  // SQL Tools Service (stubbed - will be replaced with SQLite WASM in later slice)
  sqlService: {
    invoke: async (_method: string, _params: unknown, _id?: string): Promise<unknown> => {
      console.warn('[WebDemo] sqlService.invoke not implemented');
      return null;
    },
    notify: async (_method: string, _params: unknown): Promise<boolean> => {
      return false;
    },
  },

  // Language services (stubbed)
  languageServices: {
    connectDocument: async (_args: {
      engine?: string;
      connectionId: string;
      documentUri: string;
      database?: string;
    }): Promise<{ success: boolean; error?: string; instanceKey?: string }> => ({
      success: false,
      error: 'Language services not available in browser mode. Download the desktop app for this feature.',
    }),
    disconnectDocument: async (_args: {
      engine?: string;
      documentUri: string;
      instanceKey?: string;
    }): Promise<boolean> => false,
  },

  // PostgreSQL Language Server (not available in web)
  pgsqlLanguageServer: {
    request: async (
      _instanceKey: string,
      _method: string,
      _params?: unknown
    ): Promise<{ result?: unknown; error?: string }> => ({
      error: 'PostgreSQL language server not available in browser mode. Download the desktop app for this feature.',
    }),
    // Nothing is delivered in browser mode — say so rather than claiming a
    // send. Documents never reach `isOpenOnServer` here (no `connectDocument`),
    // so the sync layer takes its local-only branch and never calls this.
    notify: async (
      _instanceKey: string,
      _method: string,
      _params?: unknown
    ): Promise<{ delivered: boolean; error?: string }> => ({
      delivered: false,
      error: 'PostgreSQL language server not available in browser mode.',
    }),
    onNotification: (
      _callback: (instanceKey: string, method: string, params: unknown) => void
    ): (() => void) => noop,
    switchContext: async (
      _connectionId: string,
      _database: string
    ): Promise<{
      success: boolean;
      switched?: boolean;
      instanceKey?: string;
      error?: string;
    }> => ({
      success: false,
      error: 'PostgreSQL not available in browser mode. Download the desktop app for this feature.',
    }),
    getContext: async (): Promise<{ connectionId: string; database: string } | null> => null,
  },

  // Database connections - SQLite connections are managed by the browser mode store
  // The actual database connection happens through BrowserModeConnectionsDialog
  connectToDatabase: async (
    connection: Connection,
    _opts?: { force?: boolean }
  ): Promise<{ success: boolean; error?: string }> => {
    if (connection.engine === 'sqlite') {
      return withSqliteDbLock(async () => {
        // If the database is already open with the same ID, return success
        if (isDatabaseOpen() && getCurrentDatabaseId() === connection.id) {
          return { success: true };
        }

        // For browser mode SQLite connections (server is 'browser-demo' or 'browser-upload'),
        // try to reconnect by loading from cache
        if (connection.server === 'browser-demo' || connection.server === 'browser-upload') {
          try {
            // First try to load from cache
            let buffer = await getCachedDatabase(connection.id);

            if (!buffer) {
              // For demo databases, try loading from asset
              const { BROWSER_MODE_DATABASES } = await import('../features/browser-mode/types/browserModeTypes');
              const demoDb = BROWSER_MODE_DATABASES.find(db => db.id === connection.id);
              if (demoDb) {
                buffer = await loadDemoDatabase(connection.id, demoDb.assetPath, () => {});
              }
            }

            if (!buffer) {
              return {
                success: false,
                error: 'Database not found in cache. Please reconnect using the connections dialog.',
              };
            }

            // Open the database from buffer
            await openDatabaseFromBuffer(connection.id, buffer);

            // Update browser mode store
            const { useBrowserModeStore } = await import('../features/browser-mode/store/browserModeStore');
            const { BROWSER_MODE_DATABASES } = await import('../features/browser-mode/types/browserModeTypes');
            const browserDb = BROWSER_MODE_DATABASES.find(db => db.id === connection.id);
            if (browserDb) {
              useBrowserModeStore.getState().setConnected(browserDb);
            } else {
              // For uploaded databases, create a minimal descriptor
              useBrowserModeStore.getState().setConnected({
                id: connection.id,
                name: connection.alias || connection.database || 'Uploaded Database',
                description: 'User-uploaded SQLite database',
                assetPath: '',
                tableCount: 0,
                sizeHint: 'Unknown',
                type: 'uploaded',
              });
            }

            return { success: true };
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to reconnect';
            return { success: false, error: message };
          }
        }

        return {
          success: false,
          error: 'Only SQLite databases are supported in browser mode. Download the desktop app for SQL Server and PostgreSQL.',
        };
      });
    }

    // If the database is already open with the same ID, return success
    if (connection.engine === 'sqlite' && isDatabaseOpen() && getCurrentDatabaseId() === connection.id) {
      return { success: true };
    }

    // For browser mode SQLite connections (server is 'browser-demo' or 'browser-upload'),
    // try to reconnect by loading from cache
    if (connection.engine === 'sqlite' && (connection.server === 'browser-demo' || connection.server === 'browser-upload')) {
      try {
        // First try to load from cache
        let buffer = await getCachedDatabase(connection.id);

        if (!buffer) {
          // For demo databases, try loading from asset
          const { BROWSER_MODE_DATABASES } = await import('../features/browser-mode/types/browserModeTypes');
          const demoDb = BROWSER_MODE_DATABASES.find(db => db.id === connection.id);
          if (demoDb) {
            buffer = await loadDemoDatabase(connection.id, demoDb.assetPath, () => {});
          }
        }

        if (!buffer) {
          return {
            success: false,
            error: 'Database not found in cache. Please reconnect using the connections dialog.',
          };
        }

        // Open the database from buffer
        await openDatabaseFromBuffer(connection.id, buffer);

        // Update browser mode store
        const { useBrowserModeStore } = await import('../features/browser-mode/store/browserModeStore');
        const { BROWSER_MODE_DATABASES } = await import('../features/browser-mode/types/browserModeTypes');
        const browserDb = BROWSER_MODE_DATABASES.find(db => db.id === connection.id);
        if (browserDb) {
          useBrowserModeStore.getState().setConnected(browserDb);
        } else {
          // For uploaded databases, create a minimal descriptor
          useBrowserModeStore.getState().setConnected({
            id: connection.id,
            name: connection.alias || connection.database || 'Uploaded Database',
            description: 'User-uploaded SQLite database',
            assetPath: '',
            tableCount: 0,
            sizeHint: 'Unknown',
            type: 'uploaded',
          });
        }

        return { success: true };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to reconnect';
        return { success: false, error: message };
      }
    }

    // For non-SQLite connections, return an error
    return {
      success: false,
      error: 'Only SQLite databases are supported in browser mode. Download the desktop app for SQL Server and PostgreSQL.',
    };
  },

  disconnectFromDatabase: async (connectionId: string): Promise<boolean> => {
    // If this is the current SQLite connection, close it
    if (getCurrentDatabaseId() === connectionId) {
      closeCurrentDatabase();
    }
    return true;
  },

  // Browser mode opens the SQLite file synchronously in-process — there is no
  // dial long enough to change your mind about, and no pool that can outlive
  // one, so there is never anything to close after the fact.
  setConnectIntent: async (): Promise<boolean> => false,

  checkConnectionStatus: async (
    connectionId: string
  ): Promise<{ state: 'connected' | 'error' | 'disconnected'; tunnelState?: 'up' | 'down' | 'connecting' | 'error' }> => {
    // Check if the connection matches our current SQLite database
    if (isDatabaseOpen() && getCurrentDatabaseId() === connectionId) {
      return { state: 'connected' };
    }
    return { state: 'disconnected' };
  },

  // Browser mode runs SQLite in-process — there is no pool to warm and no
  // network round trip to save, so reporting query intent is a no-op.
  prewarmConnection: async (): Promise<{ accepted: boolean }> => ({ accepted: false }),

  // Web shim does not run the SQLite-only diagnostics battery — return a
  // minimal stub so the renderer dialog can render the engine-not-supported
  // placeholder. The desktop build's preload provides the full implementation.
  runConnectionDiagnostics: async (args: { connectionId: string; trigger: 'list_failure' | 'pool_open_failure' | 'manual' | 'query_execution_stall'; scopeDatabase?: string }) => ({
    reportId: 'web-shim-no-uuid',
    generatedAt: Date.now(),
    connectionId: args.connectionId,
    engine: 'sqlite' as const,
    authKind: 'sql' as const,
    tenantMode: null,
    databases: { listingSucceeded: false, rawCount: 0, accessibleCount: 0 },
    triggeredBy: args.trigger,
    notImplementedFor: 'sqlite' as const,
  }),

  // Web shim does not ship diagnostic reports — return a stub so the renderer
  // can render *Sent ✓* without coupling the dialog to platform availability.
  sendDiagnosticReport: async (args: { report: { reportId?: string } }) => ({
    success: false,
    reportId: args.report?.reportId ?? 'web-shim-no-uuid',
  }),

  getTables: async (
    _connectionId: string,
    _database: string
  ): Promise<{
    tables: Array<{ id: string; name: string; schema: string; type: string }>;
    error?: string;
  }> => ({
    tables: [],
    error: 'Tables not available - connect to a SQLite database first',
  }),

  // Query execution - uses SQLite WASM when connected
  executeQueryV2: async (request: QueryExecutionRequest): Promise<QueryExecutionResult> => {
    // Execute the query using SQLite WASM
    return withSqliteDbLock(async () => {
      const ready = await ensureSqliteDatabaseOpen(request.connectionId);
      if (!ready.success) {
        return {
          success: false,
          error: { message: ready.error },
          resultSets: [],
          messages: [{ type: 'error', text: ready.error }],
        };
      }
      return executeSqliteQuery(request);
    });
  },

  cancelQueryV2: async (request: QueryCancelRequest): Promise<QueryCancelResult> => {
    // Attempt to cancel (note: sql.js runs synchronously, so this is best-effort)
    return cancelSqliteQuery(request.executionId);
  },

  // Browser mode has no MSSQL driver pool — Slice A's warm-up is a no-op.
  // Returns success so the renderer's listing path proceeds without retry.
  ensureMssqlPool: async (): Promise<{ success: boolean; code?: 'NO_POOL' }> => ({
    success: true,
  }),

  // Browser mode has no MSSQL driver pool — Slice 10's test seam is a no-op.
  // The backing IPC only ever registers in `SQL_STUDIO_TEST` Electron runs,
  // so browser callers can never observe a "real" drop here.
  dropMainPoolForTest: async (): Promise<{ success: boolean; dropped?: boolean }> => ({
    success: false,
    dropped: false,
  }),

  // Execution plan (not available in web for now)
  getExecutionPlan: async (): Promise<GetExecutionPlanResult> =>
    failResult('Execution plans not available in browser mode. Download the desktop app for this feature.'),

  importExecutionPlan: async (): Promise<ImportExecutionPlanResult> =>
    failResult('Execution plan import not available in browser mode. Download the desktop app for this feature.'),

  exportExecutionPlan: async (): Promise<ExportExecutionPlanResult> =>
    failResult('Execution plan export not available in browser mode. Download the desktop app for this feature.'),

  // Deadlock Analyzer: the browser build opens files through <input type="file">
  // (see `deadlockFileImport.ts`); paste and sample work everywhere.
  importDeadlockReport: async (): Promise<ImportDeadlockReportResult> => ({
    success: false,
    reason: 'error',
    error: 'Native file dialog not available in browser mode.',
  }),
  // Deadlock collector needs a SQL Server connection through the desktop driver.
  collectDeadlocks: async (): Promise<CollectDeadlocksResult> => ({
    success: false,
    reason: 'unsupported',
    error: 'Reading system_health deadlocks requires the desktop app. Import a saved report instead.',
  }),
  cancelDeadlockCollection: async () => ({ success: false }),

  // Results export (stubbed - will implement download in web)
  exportResults: async (): Promise<ExportResultsResult> =>
    failResult('Export not available in browser mode. Download the desktop app for export features.'),

  // Chart export (stubbed)
  exportChart: async (): Promise<ChartExportResult> =>
    failResult('Chart export not available in browser mode. Download the desktop app for this feature.'),

  // Result cache (stubbed — windowed results only available in desktop app)
  resultCache: {
    getWindow: async () => ({ rows: [], columns: [], totalRowCount: 0, generation: 0 }),
    getMetadata: async () => ({ totalRowCount: 0, columns: [], truncated: false, capabilities: { canSort: false, canFilter: false, canEdit: false, supportsAccurateCount: false } }),
    discard: async () => {},
    sort: async () => ({ totalRowCount: 0, generation: 0 }),
    filter: async () => ({ totalRowCount: 0, generation: 0 }),
    clearSort: async () => ({ totalRowCount: 0, generation: 0 }),
    clearFilter: async () => ({ totalRowCount: 0, generation: 0 }),
    // In browser mode, getCellValue returns null — full-value copy/expand is not available.
    // The renderer falls back to the preview value from the grid buffer.
    getCellValue: async () => ({ value: null, generation: 0 }),
    // In browser mode, get-column-values (batch full-value fetch for map/peek
    // decoding) is not available — resolves to an empty, non-truncated batch.
    getColumnValues: async () => ({ values: [], rowsReturned: 0, byteBudgetExceeded: false, generation: 0 }),
    exportCsv: async () => ({ success: false as const, error: 'CSV export not available in browser mode' }),
    cancelExportCsv: async () => {},
    onExportCsvProgress: () => () => {},
    onSourceProgress: () => () => {},
    cloneForPin: async () => null,
    debugInfo: async () => ({ sourceUris: [], stsRefCount: 0 }),
    __testFailSource: async () => ({ success: false, reason: 'not supported in browser shim' }),
  },

  // Query files - browser implementation using File API and Blob downloads
  queryFiles: {
    openSqlFile: async (): Promise<OpenSqlFileResult> => {
      return new Promise((resolve) => {
        // Create a hidden file input element
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.sql,.txt';
        input.style.display = 'none';

        // Handle file selection
        input.onchange = async () => {
          const file = input.files?.[0];
          if (!file) {
            resolve({ success: false, error: 'No file selected', cancelled: true });
            document.body.removeChild(input);
            return;
          }

          try {
            const query = await file.text();
            resolve({
              success: true,
              filePath: file.name,
              query,
            });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to read file';
            resolve({ success: false, error: message });
          } finally {
            document.body.removeChild(input);
          }
        };

        // Handle cancel (user closes picker without selecting)
        input.oncancel = () => {
          resolve({ success: false, error: 'File selection cancelled', cancelled: true });
          document.body.removeChild(input);
        };

        // Append to body and trigger click
        document.body.appendChild(input);
        input.click();
      });
    },
    saveSqlFile: async (request: { query: string; suggestedFileName?: string }): Promise<SaveSqlFileResult> => {
      try {
        // Create a Blob from the query content
        const blob = new Blob([request.query], { type: 'text/plain;charset=utf-8' });

        // Determine filename
        const filename = request.suggestedFileName || 'query.sql';

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        // Trigger download
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return {
          success: true,
          filePath: filename,
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save file';
        return { success: false, error: message };
      }
    },
    watchSqlFile: async () => ({ success: false }),
    unwatchSqlFile: async () => ({ success: false }),
    onExternalChange: () => () => {},
    onExternalDelete: () => () => {},
  },

  // SQLite (will be replaced with sql.js in later slice)
  sqlite: {
    openDatabaseFile: async (): Promise<OpenSqliteDatabaseFileResult> =>
      failResult('Use the upload feature on the Demo Connections page'),
  },

  // Oracle helpers (directory pickers, service discovery — not available in browser mode)
  oracle: {
    selectDirectory: async () => ({ cancelled: true }),
    discoverServices: async () => ({ error: 'Not available in browser mode' }),
    changeExpiredPassword: async () => ({
      success: false,
      error: 'Not available in browser mode',
    }),
  },

  // Oracle Package Browser (not available in browser mode)
  oraclePackage: {
    getDetails: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    compile: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getErrors: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    recompileInvalid: async () => ({ success: false as const, total: 0, succeeded: 0, failed: 0, error: 'Not available in browser mode' }),
    getDependencies: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listPackageStatuses: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // Oracle PL/SQL Debugger (not available in browser mode)
  oracleDebugger: {
    checkPrivileges: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    init: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    attach: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    detach: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    compileForDebug: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    checkDebugCompilation: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getObjectSource: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    setBreakpoint: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    deleteBreakpoint: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    enableBreakpoint: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    disableBreakpoint: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    start: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    continue: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    stop: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getSourceForProgram: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getVariables: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getCallStack: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    evaluateExpression: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    setExceptionBreakpoint: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    clearExceptionBreakpoint: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    setBreakpointCondition: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    setVariable: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listSessions: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    attachToSession: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    waitForRemoteBreakpoint: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    checkProtocolAvailability: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // Oracle JDWP ACL Setup (not available in browser mode)
  oracleJdwp: {
    checkPrerequisites: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getSetupCommands: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    testConnectivity: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // Oracle PL/SQL Profiler (not available in browser mode)
  oracleProfiler: {
    start: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    stop: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getResults: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // Oracle PL/SQL Editor (not available in browser mode)
  plsqlEditor: {
    compile: async () => ({ success: false as const, errors: [], error: 'Not available in browser mode' }),
  },

  // Debug Tool Confirmation (not available in browser mode)
  debugConfirmation: {
    onRequest: () => () => {},
    sendResponse: () => {},
    clearAutoApproved: async () => {},
  },

  // Query Store (SQL Server catalog reads need the desktop app) — reports `unsupported` gracefully
  queryStore: {
    getCapability: async (): Promise<QueryStoreCapabilityResult> => ({
      success: true,
      capability: { state: 'unsupported', serviceKind: 'other', message: 'Query Store browsing is not available in browser mode. Download the desktop app for this feature.' },
    }),
    compare: async (): Promise<QueryStoreCompareResult> => ({ success: false, errorState: 'unsupported', error: 'Not available in browser mode' }),
    getDetail: async (): Promise<QueryStoreDetailResult> => ({ success: false, error: 'Not available in browser mode' }),
    getPlan: async (): Promise<QueryStorePlanResult> => ({ success: false, error: 'Not available in browser mode' }),
    cancel: async () => {},
  },

  // Session Browser (not available in browser mode)
  sessionBrowser: {
    getSessions: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getDetail: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    killSession: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getOpenCursors: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getLocks: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getWaitEvents: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getLockStats: async () => ({ success: false as const, rows: [], error: 'Not available in browser mode' }),
  },

  // Oracle Session Browser (backward-compat alias, not available in browser mode)
  oracleSessions: {
    getSessions: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getDetail: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    killSession: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getOpenCursors: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getLocks: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getWaitEvents: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // Job Scheduler (not available in browser mode)
  jobScheduler: {
    getSupportedEngines: async () => ({ success: true as const, engines: [] as Array<{ engine: string; explorerLabel: string; toolbarLabel: string }> }),
    listJobs: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getJobDetail: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getJobHistory: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getHistorySummary: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    startJob: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    runningStatus: async () => ({ success: false as const, isRunning: false as const, error: 'Not available in browser mode' }),
    stopJob: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    toggleJobEnabled: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    createJob: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    updateJob: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    deleteJob: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    generateScript: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getCategories: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getOperators: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getDatabases: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listSchedules: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listOperators: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getOperatorNotifications: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    createOperator: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    updateOperator: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    deleteOperator: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listAlerts: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    createAlert: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    updateAlert: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    deleteAlert: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    toggleAlertEnabled: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    alertHistory: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listProxies: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listCredentials: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listProxyPrincipals: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listServerLogins: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    createProxy: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    updateProxy: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    deleteProxy: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getAgentProperties: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    updateAgentProperties: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listMailProfiles: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getErrorLog: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // MSSQL Filegroup Manager (not available in browser mode)
  filegroupManager: {
    getFilegroups: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getDatabaseFiles: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    generateScript: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // Oracle Tablespace Manager (not available in browser mode)
  oracleTablespaces: {
    getTablespaces: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getDatafiles: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getIOStatistics: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getUndoInfo: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    generateScript: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // Oracle Object Detail (not available in browser mode)
  oracleObjects: {
    getDetail: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    executeAction: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // Oracle Instance Monitor (not available in browser mode)
  oracleInstance: {
    getInfo: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getStats: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getTopSql: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getAlertLog: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getAwrSnapshots: async () => ({ success: false as const, licensed: false, error: 'Not available in browser mode' }),
    generateAwrReport: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // Performance Dashboard (not available in browser mode)
  performanceDashboard: {
    getInfo: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getKeyStats: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getTopSql: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getErrorLog: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getWaitStats: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getAwrSnapshots: async () => ({ success: false as const, licensed: false, error: 'Not available in browser mode' }),
    generateAwrReport: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getAutovacuum: async () => ({ success: false as const, rows: [], error: 'Not available in browser mode' }),
  },

  // Oracle Data Pump (not available in browser mode)
  oracleDataPump: {
    getDirectories: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getSchemas: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getTables: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    checkPrerequisites: async () => ({ success: false as const, hasExportRole: false, hasImportRole: false, directories: [], error: 'Not available in browser mode' }),
    generateExportScript: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    generateImportScript: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getJobs: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    getJobProgress: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    executeJobAction: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },

  // Transaction management (not available in browser mode)
  transaction: {
    begin: async () => ({ success: false as const, error: 'Transactions are not available in browser mode' }),
    abandonBegin: async () => ({ success: false }),
    commit: async () => ({ success: false as const, error: 'Transactions are not available in browser mode' }),
    rollback: async () => ({ success: false as const, error: 'Transactions are not available in browser mode' }),
    release: async () => ({ success: false as const, error: 'Transactions are not available in browser mode' }),
    executeBatch: async () => ({ status: 'rolled-back' as const, error: 'Transactions are not available in browser mode' }),
    onMcpStateSync: () => () => { /* no-op in browser mode */ },
    onSessionsReleased: () => () => { /* no-op in browser mode */ },
  },

  // Notebook file operations (not available in browser mode)
  notebookFile: {
    save: async () => ({ success: false as const, error: 'Notebook file save is not available in browser mode' }),
    open: async () => ({ success: false as const, error: 'Notebook file open is not available in browser mode' }),
    exportMarkdown: async () => ({ success: false as const, error: 'Notebook export is not available in browser mode' }),
    exportPdf: async () => ({ success: false as const, error: 'Notebook export is not available in browser mode' }),
  },

  // JavaScript execution (not available in browser mode)
  jsExecution: {
    execute: async () => ({
      success: false, consoleOutput: [], error: 'JavaScript execution is not available in browser mode', durationMs: 0,
    }),
  },

  // Notebook session management (not available in browser mode)
  notebookSession: {
    acquire: async () => ({ success: false as const, error: 'Notebook sessions are not available in browser mode' }),
    execute: async () => ({ success: false as const, error: { message: 'Notebook sessions are not available in browser mode' } }),
    release: async () => ({ success: false as const, error: 'Notebook sessions are not available in browser mode' }),
    status: async () => ({ hasSession: false as const }),
  },

  // Table metadata for SQLite
  getTableMetadata: async (request: GetTableMetadataRequest): Promise<TableMetadataResult> => {
    return withSqliteDbLock(async () => {
      const ready = await ensureSqliteDatabaseOpen(request.connectionId);
      if (!ready.success) {
        return { success: false, error: ready.error };
      }

      const result = getTableMetadataSqlite(request.tableName);
      if (!result.success) {
        return { success: false, error: result.error };
      }

      // Extract primary key columns from metadata
      const primaryKeyColumns = result.columns
        ?.filter((col) => col.isPrimaryKey)
        .map((col) => col.name) || [];

      // Build constraints array with PRIMARY KEY constraint if present
      // This is required for useTableMetadata to detect primary keys
      const constraints = primaryKeyColumns.length > 0
        ? [{
            name: 'PRIMARY',
            type: 'PRIMARY KEY' as const,
            columns: primaryKeyColumns,
          }]
        : [];

      // Map TableExplorerColumn to the expected ColumnMetadata format
      return {
        success: true,
        metadata: {
          columns: result.columns?.map((col) => ({
            name: col.name,
            dataType: col.dataType,
            isNullable: col.nullable,
            isPrimaryKey: col.isPrimaryKey,
            isIdentity: col.isIdentity || false,
          })) || [],
          constraints,
        },
      };
    });
  },

  getDatabaseMetadata: async (): Promise<DatabaseMetadataResult> =>
    failResult('Database metadata not available in browser mode'),

  // Routine metadata (function / stored procedure parameters) — browser mode
  // doesn't have SQL Server, so signature help for functions is not supported.
  // Return a successful response with null metadata so the client-side
  // signature-help provider cleanly shows nothing.
  getRoutineMetadata: async () => ({ success: true, metadata: null }),

  // Table Explorer (SQLite implementation for browser mode)
  tableExplorer: {
    fetchRows: async (request: TableExplorerFetchRowsRequest) =>
      withSqliteDbLock(async () => {
        const ready = await ensureSqliteDatabaseOpen(request.connectionId);
        if (!ready.success) {
          return { success: false, error: ready.error, rows: [], totalRowCount: 0 };
        }
        return fetchTableRows(request);
      }),
    previewSelect: async (request: TableExplorerPreviewSelectRequest) =>
      // previewSelect is a pure SQL generator and does not require an open database.
      // Keep it usable for tests and UI previews even before connecting.
      previewSelect(request),
    previewChanges: async (request: TableExplorerPreviewChangesRequest) =>
      withSqliteDbLock(async () => {
        const ready = await ensureSqliteDatabaseOpen(request.connectionId);
        if (!ready.success) {
          return { success: false, error: ready.error };
        }
        return previewChanges(request);
      }),
    applyChanges: async (request: TableExplorerApplyChangesRequest) =>
      withSqliteDbLock(async () => {
        const ready = await ensureSqliteDatabaseOpen(request.connectionId);
        if (!ready.success) {
          return { success: false, error: ready.error };
        }
        return applyChanges(request);
      }),
    fetchInboundForeignKeys: async (request: TableExplorerFetchInboundForeignKeysRequest) =>
      withSqliteDbLock(async () => {
        const ready = await ensureSqliteDatabaseOpen(request.connectionId);
        if (!ready.success) {
          return { success: false, error: ready.error, foreignKeys: [] };
        }
        return fetchInboundForeignKeys(request);
      }),
    fetchCellValue: async () => ({ success: false, error: 'Cell value fetch not available in browser mode' }),
  },

  // Security Management (not available in web)
  securityManagement: {
    fetchLogins: async () => ({ success: false, error: 'Not available in browser mode. Download the desktop app for this feature.' }),
    fetchServerRoles: async () => ({ success: false, error: 'Not available in browser mode. Download the desktop app for this feature.' }),
    fetchUsers: async () => ({ success: false, error: 'Not available in browser mode. Download the desktop app for this feature.' }),
    fetchDatabaseRoles: async () => ({ success: false, error: 'Not available in browser mode. Download the desktop app for this feature.' }),
    fetchSchemas: async () => ({ success: false, error: 'Not available in browser mode. Download the desktop app for this feature.' }),
    fetchRoleMembership: async () => ({ success: false, error: 'Not available in browser mode. Download the desktop app for this feature.' }),
    fetchPrincipalPermissions: async () => ({ success: false, error: 'Not available in browser mode. Download the desktop app for this feature.' }),
    fetchSecurityPrincipals: async () => ({ success: false, error: 'Not available in browser mode. Download the desktop app for this feature.' }),
    previewScript: async () => ({ success: false, error: 'Not available in browser mode. Download the desktop app for this feature.' }),
    applyScript: async () => ({ success: false, error: 'Not available in browser mode. Download the desktop app for this feature.' }),
  } as SecurityManagementAPI,

  // Native application menu — desktop only; browser mode has no menu bar.
  appMenu: {
    onCommand: (_callback: (payload: {
      commandId: string;
      source: 'click' | 'accelerator';
      accelerator?: string;
    }) => void): (() => void) => noop,
    setConfig: async (_menuConfig: {
      hiddenCommandIds: readonly string[];
      accelerators: ReadonlyMap<string, string>;
    }) => ({ success: true }),
  },

  // Window chrome — the browser owns the frame, so the toolbar reserves nothing
  // and there are no fullscreen transitions to report.
  windowChrome: {
    mode: 'native' as const,
    platform: 'browser',
    contentInsetLeft: 0,
    onFullscreenChanged: (_callback: (isFullscreen: boolean) => void): (() => void) => noop,
  },

  // Session (use beforeunload in web)
  session: {
    onAppClosing: (_callback: () => void): (() => void) => {
      // In web, we can use beforeunload but it's limited
      // For now, just return a noop cleanup
      return noop;
    },
    confirmClose: (): void => {},
    onAppClosingForUpdate: (_callback: () => void): (() => void) => {
      // Not applicable in web mode - auto-updates are desktop only
      return noop;
    },
    confirmCloseForUpdate: async (): Promise<{ success: boolean }> => {
      // Not applicable in web mode - auto-updates are desktop only
      return { success: false };
    },
  },

  // Entra Auth (not available in web)
  entraAuth: {
    onEvent: (_callback: unknown): (() => void) => noop,
    abortInteractive: async (_connectionId: string): Promise<boolean> => false,
  },

  // LogTo OIDC auth (not available in web demo)
  auth: {
    initialize: async () => ({ user: null }),
    signIn: async () => ({ success: false, error: 'Not available in browser mode' }),
    signOut: async () => ({ success: true }),
    getAccessToken: async () => ({ token: null }),
    onAuthStateChanged: (_callback: unknown): (() => void) => noop,
  },

  // Open external URL - in browser mode, use window.open
  openExternalUrl: async (url: string): Promise<{ success: boolean; error?: string }> => {
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to open URL',
      };
    }
  },

  // Show item in folder - no-op in browser mode
  showItemInFolder: async (_path: string): Promise<{ success: boolean; error?: string }> => {
    console.warn('[WebShim] showItemInFolder not available in browser mode');
    return { success: false, error: 'Not available in browser mode' };
  },

  // Open logs folder - no filesystem access in browser mode
  openLogsFolder: async (): Promise<{ success: boolean; error?: string }> => {
    console.warn('[WebShim] openLogsFolder not available in browser mode');
    return { success: false, error: 'Not available in browser mode' };
  },

  getPathForFile: (_file: File): string => {
    console.warn('[WebShim] getPathForFile not available in browser mode');
    return '';
  },

  getHomeDir: (): string => {
    // No filesystem access in browser mode — returning '' makes any
    // path-collapse helper effectively a no-op.
    return '';
  },

  // Clipboard utilities - best-effort in browser mode
  clipboard: {
    writeText: async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard?.writeText?.(text);
        return true;
      } catch {
        // Browser mode (e.g. "Document is not focused") — let the caller fall back.
        return false;
      }
    },
    writeRich: async (text: string, html: string): Promise<boolean> => {
      try {
        const item = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });
        await navigator.clipboard?.write?.([item]);
        return true;
      } catch {
        try {
          // Fall back to plain text.
          await navigator.clipboard?.writeText?.(text);
          return true;
        } catch {
          return false;
        }
      }
    },
    readText: async (): Promise<string> => {
      try {
        return (await navigator.clipboard?.readText?.()) ?? '';
      } catch {
        return '';
      }
    },
  },

  // License cache (no-op in web - no offline mode)
  licenseCache: {
    validateCache: async () => ({
      isValid: false,
      hasProAccess: false,
      daysRemaining: 0,
      showWarning: false,
      error: 'License cache not available in browser mode',
      clockValidation: {
        isValid: true,
        rollbackDetected: false,
      },
      blockedByClockRollback: false,
    }),
    validateCacheAsync: async () => ({
      isValid: false,
      hasProAccess: false,
      daysRemaining: 0,
      showWarning: false,
      error: 'License cache not available in browser mode',
      clockValidation: {
        isValid: true,
        rollbackDetected: false,
      },
      blockedByClockRollback: false,
    }),
    updateCache: async () => ({
      lastValidatedAt: new Date().toISOString(),
      cachedUntil: new Date().toISOString(),
      subscriptionStatus: 'none' as const,
    }),
    updateCacheWithSignedToken: async () => true,
    clearCache: async () => true,
    getCacheStatus: async () => ({
      lastValidated: null,
      expiresAt: null,
      daysRemaining: 0,
      status: 'none' as const,
    }),
    readSignedToken: async () => null,
    hasValidSignedToken: async () => false,
  },

  // License Entitlement (browser mode always shows Pro for demo purposes)
  entitlement: {
    get: async () => ({
      isPro: true, // Browser demo has Pro access to showcase features
      mode: { type: 'pro' as const },
      features: {
        'session-management': true,
        'full-query-history': true,
        'ai-workspace': true,
        'schema-advanced-layout': true,
        'schema-export-formats': true,
        'impact-analysis': true,
        'schema-annotations': true,
        'schema-comparison': true,
        'schema-overview-full-selection': true,
        'data-compare-full': true,
        'schema-compare-full': true,
        'database-blueprint-large': true,
        'backup-restore-full': true,
        'clone-database-full': true,
        'clone-table-full': true,
        'export-full': true,
        'data-import': true,
        'ai-workspace-full-selection': true,
        'dashboard-html-snapshot': true,
      },
      source: 'subscription' as const, // Simulates Pro access
      trialDaysRemaining: null,
      gracePeriodDaysRemaining: null,
      isOffline: false,
      clockValidation: {
        isValid: true,
        rollbackDetected: false,
      },
      blockedByClockRollback: false,
    }),
    syncTrialData: async () => ({
      isPro: true,
      mode: { type: 'pro' as const },
      features: {
        'session-management': true,
        'full-query-history': true,
        'ai-workspace': true,
        'schema-advanced-layout': true,
        'schema-export-formats': true,
        'impact-analysis': true,
        'schema-annotations': true,
        'schema-comparison': true,
        'schema-overview-full-selection': true,
        'data-compare-full': true,
        'schema-compare-full': true,
        'database-blueprint-large': true,
        'backup-restore-full': true,
        'clone-database-full': true,
        'clone-table-full': true,
        'export-full': true,
        'data-import': true,
        'ai-workspace-full-selection': true,
        'dashboard-html-snapshot': true,
      },
      source: 'subscription' as const,
      trialDaysRemaining: null,
      gracePeriodDaysRemaining: null,
      isOffline: false,
      clockValidation: {
        isValid: true,
        rollbackDetected: false,
      },
      blockedByClockRollback: false,
    }),
    syncSubscriptionData: async () => ({
      isPro: true,
      mode: { type: 'pro' as const },
      features: {
        'session-management': true,
        'full-query-history': true,
        'ai-workspace': true,
        'schema-advanced-layout': true,
        'schema-export-formats': true,
        'impact-analysis': true,
        'schema-annotations': true,
        'schema-comparison': true,
        'schema-overview-full-selection': true,
        'data-compare-full': true,
        'schema-compare-full': true,
        'database-blueprint-large': true,
        'backup-restore-full': true,
        'clone-database-full': true,
        'clone-table-full': true,
        'export-full': true,
        'data-import': true,
        'ai-workspace-full-selection': true,
        'dashboard-html-snapshot': true,
      },
      source: 'subscription' as const,
      trialDaysRemaining: null,
      gracePeriodDaysRemaining: null,
      isOffline: false,
      clockValidation: {
        isValid: true,
        rollbackDetected: false,
      },
      blockedByClockRollback: false,
    }),
    clear: async () => {},
    canUseFeature: async () => true, // All features available in browser demo
    getState: async () => ({
      trial: {
        startedAt: null,
        expiresAt: null,
        skipped: false,
      },
      subscription: {
        status: 'active' as const,
        currentPeriodEnd: null,
        userId: null,
      },
      userId: null,
      lastSyncAt: null,
    }),
  },

  // Device Registration (returns mock device info in browser mode)
  device: {
    getInfo: async () => ({
      fingerprint: 'browser-demo-device',
      deviceName: 'Browser Demo',
      platform: 'unknown' as const,
      createdAt: new Date().toISOString(),
    }),
  },

  // Terminal (not available in web)
  terminal: {
    create: async (): Promise<TerminalCreateResult> =>
      failResult('Terminal not available in browser mode. Download the desktop app for terminal access.'),
    write: async (): Promise<TerminalWriteResult> => failResult('Terminal not available'),
    resize: async (): Promise<TerminalResizeResult> => failResult('Terminal not available'),
    close: async (): Promise<TerminalCloseResult> => ({ success: true }),
    retryExport: async (): Promise<TerminalRetryExportResult> => failResult('Terminal not available'),
    confirmFilter: async (): Promise<TerminalConfirmFilterResult> => failResult('Terminal not available'),
    cancelExport: async (): Promise<TerminalCancelExportResult> => failResult('Terminal not available'),
    // No CLI agents are detectable in browser mode — the Start buttons simply
    // never render (empty list, not a failResult).
    detectedAgents: async (): Promise<DetectedAgent[]> => [],
    // Unreachable in practice: the add-skill nudge (AddSkillNudgeRow) gates
    // on `useCliDetection()`, which reads `aiChat.detectCli()` — that stub
    // below always returns found:false for both agents in browser mode, so
    // the nudge never has a detected agent to check this for in the first
    // place. `false` here is a harmless placeholder, not a real safety net.
    isSkillInstalled: async (): Promise<boolean> => false,
    onOutput: (_callback: unknown): (() => void) => noop,
    onExit: (_callback: unknown): (() => void) => noop,
    onInitProgress: (_callback: unknown): (() => void) => noop,
    onLargeDbDetected: (_callback: unknown): (() => void) => noop,
  },

  // Test mode helpers
  testMode: {
    isMockEngineEnabled: (): boolean => false,
    isTestMode: (): boolean => false,
    getTestExportDir: (): string | null => null,
  },

  // Backup/Restore (not available in web)
  backupRestore: {
    mssqlExport: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    mssqlImport: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    mssqlBackupBak: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    mssqlRestoreBak: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    mssqlCancelBak: async (): Promise<{ success: boolean; error?: string }> => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    mssqlGetDefaultBackupDirectory: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    mssqlListServerFiles: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    checkDatabaseExists: async (): Promise<{ exists: boolean; error?: string }> => ({
      exists: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    pgsqlBackup: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    pgsqlCheckTools: async (): Promise<{
      available: boolean;
      path?: string;
      version?: string;
      error?: string;
    }> => ({
      available: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    pgsqlRestore: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    pgsqlCheckRestoreTools: async (): Promise<{
      available: boolean;
      pgRestorePath?: string;
      psqlPath?: string;
      error?: string;
    }> => ({
      available: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    onTaskProgress: (_callback: unknown): (() => void) => noop,
    getDatabaseSize: async (): Promise<{ success: boolean; sizeInBytes?: number; error?: string }> => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
  },

  tableDesigner: {
    planSave: async () => { throw new Error('Saving Table Designer changes requires the desktop app.'); },
  },
  // Schema compare (not available in web)
  schemaCompare: {
    fetchSchemaSnapshot: async () =>
      failResult('Not available in browser mode. Download the desktop app for this feature.'),
    runCompare: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    rediff: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    clearTabCache: async () => ({ success: false }),
    cancel: async () => ({ success: false }),
    fetchDdl: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    generateScript: async () =>
      failResult('Not available in browser mode. Download the desktop app for this feature.'),
    getObjectCounts: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    // W2.3 — fail open (no warning): the feature is unavailable in browser
    // mode anyway, so there is nothing to warn about.
    checkPermissionPreflight: async () => ({ hasDefinitionVisibility: true }),
    exportOptionsFile: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    importOptionsFile: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    onProgress: (_callback: unknown): (() => void) => noop,
  },

  // Data compare (not available in web)
  dataCompare: {
    start: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    cancel: async () => ({ success: false }),
    getDiffPage: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    getRowDiff: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    clear: async () => ({ success: false }),
    onProgress: (_callback: unknown): (() => void) => noop,
    getAllRowKeys: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    generateScript: async () =>
      failResult('Not available in browser mode. Download the desktop app for this feature.'),
    estimateRowCount: async () => ({ success: false, error: 'Not available in browser mode', rowCount: null }),
    migrateTableCrossEngine: async () => ({
      success: false,
      warnings: [],
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    migrateDatabaseCrossEngine: async () => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
      results: [],
      tableCount: 0,
      migratedCount: 0,
      failedCount: 0,
      collisionCount: 0,
    }),
    migrateCrossEngineStart: async (request: { scope: 'table' | 'database' }) => ({
      success: false,
      scope: request.scope,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
      createdTables: [],
    }),
    migrateCrossEngineCancel: async () => ({ success: false }),
    migrateCrossEngineCleanup: async () => ({ success: false, dropped: [], failures: [] }),
    onMigrateProgress: (_callback: unknown): (() => void) => noop,
  },

  // Clone database/table (not available in web)
  cloneDatabase: {
    start: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    cancel: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    getLatestProgress: async () => null,
    checkTargetExists: async (): Promise<{ exists: boolean; error?: string }> => ({
      exists: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    onProgress: (_callback: unknown): (() => void) => noop,
  },

  cloneTable: {
    start: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    cancel: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    checkExists: async (): Promise<{ exists: boolean; error?: string }> => ({
      exists: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    previewScript: async () =>
      failResult('Not available in browser mode. Download the desktop app for this feature.'),
    onProgress: (_callback: unknown): (() => void) => noop,
  },

  // Data Import Wizard (not available in web)
  dataImport: {
    browseFile: async () => ({ canceled: true as const }),
    analyze: async () => ({
      success: false as const,
      error: 'data-import:not-available',
      message: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    // Native-type projection needs the main-process `renderColumnType` — not
    // reachable from browser mode, so this returns an empty projection/preset
    // list rather than inventing an error variant the response type doesn't have.
    projectTypes: async () => ({ nativeTypes: [] }),
    typePresets: async () => ({ presets: [] }),
    fetchTargetColumns: async () => ({
      success: false as const,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    cancel: async () => ({ success: false }),
    run: async () => ({
      success: false as const,
      error: 'data-import:not-available',
      message: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    saveErrorReport: async () => ({ canceled: true as const }),
    dropTable: async () => ({
      success: false,
      error: 'data-import:not-available',
      message: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    onProgress: (_callback: unknown): (() => void) => noop,
    onAnalysisPartial: (_callback: unknown): (() => void) => noop,
  },

  // Test helpers (not available in web)
  createTestSqliteDatabase: async () =>
    failResult('Not available in browser mode. Download the desktop app for this feature.'),

  // Dependency viewer (SQLite implemented for browser mode)
  getDependencies: async (request: {
    connectionId: string;
    database: string;
    schema: string;
    objectName: string;
    objectType: string;
  }) => {
    // Only support table and view dependencies for SQLite
    const supportedTypes = ['table', 'view'];
    if (!supportedTypes.includes(request.objectType)) {
      return {
        success: true,
        dependencies: { uses: [], usedBy: [] },
        limitedSupport: true,
      };
    }

    return withSqliteDbLock(async () => {
      const ready = await ensureSqliteDatabaseOpen(request.connectionId);
      if (!ready.success) {
        return { success: false, error: ready.error };
      }

      return getSqliteDependencies(
        request.objectName,
        request.objectType as 'table' | 'view'
      );
    });
  },

  // Schema overview - SQLite implementation for browser mode
  getSchemaOverview: async (request: { connectionId: string }) => {
    // Use SQLite schema overview for browser mode
    return withSqliteDbLock(async () => {
      const ready = await ensureSqliteDatabaseOpen(request.connectionId);
      if (!ready.success) {
        return { success: false, error: ready.error };
      }
      return getSqliteSchemaOverview();
    });
  },

  // Schema overview counts - lightweight for size check
  getSchemaOverviewCounts: async (request: { connectionId: string; database: string }) => {
    // For browser mode, return mock counts that won't trigger the dialog
    return withSqliteDbLock(async () => {
      const ready = await ensureSqliteDatabaseOpen(request.connectionId);
      if (!ready.success) {
        return { success: false, error: ready.error };
      }
      // Return low counts so filter dialog doesn't show in browser demo
      return {
        success: true,
        data: {
          tableCount: 10,
          viewCount: 5,
          procedureCount: 0,
          functionCount: 0,
        },
      };
    });
  },

  // Schema object names - for filter dialog
  getSchemaObjectNames: async (_request: { connectionId: string; database: string; objectType: string }) => {
    // For browser mode, return empty array
    return {
      success: true,
      data: [],
    };
  },

  // Schema export - browser download implementation
  exportSchemaOverview: async (request: { imageData: string; suggestedFileName?: string }) => {
    try {
      // Extract the base64 data from the data URL
      const dataUrl = request.imageData;

      // Create a link element for downloading
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = request.suggestedFileName || 'schema.png';
      link.style.display = 'none';

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);

      return {
        success: true,
        filePath: link.download, // In browser, we just return the filename
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export schema';
      return {
        success: false,
        error: message,
      };
    }
  },

  // VQE grammar-harness — browser mode just no-ops with a synthetic path so
  // the renderer's confirmation flow still completes; nothing is persisted
  // to disk in browser mode.
  writeGrammarHarnessReport: async (request: { reportId: string; createdAt: number }) => {
    return {
      success: true,
      filePath: `(browser-mode, not persisted) ${request.createdAt}-${request.reportId}.txt`,
    };
  },

  // Schema annotations (localStorage for web mode)
  schemaAnnotations: {
    get: async (request: { connectionId: string; database: string }) => {
      try {
        const key = `schema-annotations:${request.connectionId}:${request.database}`;
        const stored = localStorage.getItem(key);
        const annotations = stored ? JSON.parse(stored) : {};
        return { success: true, annotations };
      } catch {
        return { success: true, annotations: {} };
      }
    },
    save: async (request: {
      connectionId: string;
      database: string;
      objectId: string;
      note: string;
    }) => {
      try {
        const key = `schema-annotations:${request.connectionId}:${request.database}`;
        const stored = localStorage.getItem(key);
        const annotations = stored ? JSON.parse(stored) : {};

        const now = new Date().toISOString();
        const existing = annotations[request.objectId];

        const annotation = {
          id: existing?.id ?? crypto.randomUUID(),
          objectId: request.objectId,
          note: request.note,
          createdAt: existing?.createdAt ?? now,
          updatedAt: now,
        };

        annotations[request.objectId] = annotation;
        localStorage.setItem(key, JSON.stringify(annotations));

        return { success: true, annotation };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save annotation';
        return { success: false, error: message };
      }
    },
    delete: async (request: {
      connectionId: string;
      database: string;
      objectId: string;
    }) => {
      try {
        const key = `schema-annotations:${request.connectionId}:${request.database}`;
        const stored = localStorage.getItem(key);
        if (stored) {
          const annotations = JSON.parse(stored);
          delete annotations[request.objectId];
          localStorage.setItem(key, JSON.stringify(annotations));
        }
        return { success: true };
      } catch {
        return { success: true }; // Ignore errors on delete
      }
    },
  },

  // Dashboards — not available in web mode. Mutators honor the RepoResult
  // contract (the store awaits results; a throw here would surface as an
  // unhandled rejection with zero UI feedback).
  dashboards: {
    list: async () => ({ dashboards: [], warnings: [] }),
    create: async () => dashboardsUnavailable(),
    duplicate: async () => dashboardsUnavailable(),
    importDefinition: async () => dashboardsUnavailable(),
    save: async () => dashboardsUnavailable(),
    addTile: async () => dashboardsUnavailable(),
    updateTile: async () => dashboardsUnavailable(),
    removeTile: async () => dashboardsUnavailable(),
    delete: async () => dashboardsUnavailable(),
    getUiState: async () => ({ version: 1 as const }),
    setUiState: async () => {},
    onChanged: () => () => {},
    runTile: async () => ({
      kind: 'result' as const,
      result: {
        ok: false as const,
        executionId: 'web-noop',
        error: 'Dashboards are unavailable in web mode',
      },
    }),
    cancelTileRun: async () => ({ success: true }),
    grantScheduleTrust: async () => dashboardsUnavailable(),
    onRefreshRequest: () => () => {},
    sendRefreshReply: () => {},
    onRuntimeSnapshotRequest: () => () => {},
    sendRuntimeSnapshotReply: () => {},
    getAiSchemaContext: async () => ({ tables: [], truncated: false }),
    notificationsSupported: async () => false,
    notifyAlert: async () => ({ sent: false }),
    exportDefinition: async () => ({
      success: false as const,
      error: 'Dashboard export is unavailable in web mode',
    }),
    readDefinitionFile: async () => ({
      success: false as const,
      error: 'Dashboard import is unavailable in web mode',
    }),
    capturePng: async () => ({
      success: false as const,
      error: 'Dashboard capture is unavailable in web mode',
    }),
    captureTile: async () => ({
      success: false as const,
      error: 'Dashboard capture is unavailable in web mode',
    }),
    capturePdf: async () => ({
      success: false as const,
      error: 'Dashboard capture is unavailable in web mode',
    }),
    exportHtmlSnapshot: async () => ({
      success: false as const,
      error: 'Dashboard export is unavailable in web mode',
    }),
  },
  metainfo: {
    get: async () => null,
    listAll: async () => [],
    addLooseRelationship: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    updateLooseRelationship: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    removeLooseRelationship: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    addJsonColumn: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    removeJsonColumn: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    upsertJsonStructureCache: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    addEnumColumn: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    removeEnumColumn: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    addSpatialColumn: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    removeSpatialColumn: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    addEnumGlyphDismissal: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    removeEnumGlyphDismissal: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    updateEnumColumn: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    upsertEnumValueCache: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    removeEnumValueCache: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    fetchEnumValues: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    upsertTableDisplayColumn: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    removeTableDisplayColumn: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    addReferenceTable: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    removeReferenceTable: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    upsertJoinTableHint: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    removeJoinTableHint: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    export: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    import: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    applyMetaInfoDelta: async () => {
      throw new Error('metainfo IPC unavailable (web mode)');
    },
    onWritten: () => {
      // No-op in web mode — events only flow in the Electron context.
      return () => {};
    },
  },

  // MSSQL-specific helpers — not available in web mode
  mssql: {
    getDatabaseCompatLevel: async () => null,
    forgetDatabaseCompatLevel: async () => true,
  },

  // AI Workspace (not available in web)
  aiWorkspace: {
    updateSettings: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    startWatching: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    stopWatching: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    syncActiveSelection: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    syncActiveTabSql: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    syncContext: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    onSelectionRequested: (_callback: unknown): (() => void) => noop,
    onActiveTabSqlChanged: (_callback: unknown): (() => void) => noop,
    saveHistory: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    clearHistory: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    listHistory: async (): Promise<{
      success: boolean;
      entries?: Array<{ baseName: string; timestamp: string; tabName: string }>;
      error?: string;
    }> => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    updateLicenseMode: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    setWorkspacePersistence: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    // Multi-Tab Sync (v1.1) - all stubbed for browser mode
    syncTabFile: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    deleteTabFile: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    syncAllTabs: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    syncActiveTabJson: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    writeAbortRequest: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    // Lock state mirroring (v1.2) - stubbed for browser mode
    writeTabLock: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    deleteTabLock: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    updateTabAbortState: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    onTabFileCreated: (_callback: unknown): (() => void) => noop,
    onTabFileChanged: (_callback: unknown): (() => void) => noop,
    onTabFileDeleted: (_callback: unknown): (() => void) => noop,
    onActiveTabSwitch: (_callback: unknown): (() => void) => noop,
    onTabMetaChanged: (_callback: unknown): (() => void) => noop,
    onTabMetaRemoved: (_callback: unknown): (() => void) => noop,
    // Execute-with-confirm protocol - stubbed for browser mode
    onExecutionRequest: (_callback: unknown): (() => void) => noop,
    sendExecutionDecision: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
    sendExecutionFinished: async () => failResult('Not available in browser mode. Download the desktop app for this feature.'),
  },

  // MCP Server (not available in web)
  mcpServer: {
    getConfig: async () => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    getStatusLight: async () => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    updateConfig: async () => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    rotateToken: async () => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    getStatus: async () => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    start: async () => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    stop: async () => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
    getAuditLog: async () => ({
      success: false,
      error: 'Not available in browser mode. Download the desktop app for this feature.',
    }),
  },
  // MCP App Context (not available in web)
  mcpAppContext: {
    updateAll: async () => ({ success: true }),
    updateWorkspace: async () => ({ success: true }),
    updateConnections: async () => ({ success: true }),
    updateTabs: async () => ({ success: true }),
    updateEditor: async () => ({ success: true }),
    updateResults: async () => ({ success: true }),
    updateSelection: async () => ({ success: true }),
  },

  // MCP UI Control (not available in web)
  mcpUiControl: {
    onExecuteCommand: (_callback: unknown): (() => void) => noop,
    sendResult: (_commandId: string, _result: unknown): void => {},
    onConfirmationRequest: (_callback: unknown): (() => void) => noop,
    sendConfirmationResponse: (_requestId: string, _approved: boolean): void => {},
    onWriteApprovalRequest: (_callback: unknown): (() => void) => noop,
    sendWriteApprovalResponse: (_requestId: string, _approved: boolean): void => {},
    onStorageWriteApprovalRequest: (_callback: unknown): (() => void) => noop,
    sendStorageWriteApprovalResponse: (_requestId: string, _approved: boolean): void => {},
    onStorageWriteApprovalDismissed: (_callback: unknown): (() => void) => noop,
    getPendingStorageWriteApproval: async (): Promise<null> => null,
    onPairingApprovalRequest: (_callback: unknown): (() => void) => noop,
    sendPairingApprovalResponse: (_requestId: string, _approved: boolean): void => {},
    onConnectionsAddApprovalRequest: (_callback: unknown): (() => void) => noop,
    sendConnectionsAddApprovalResponse: (_requestId: string, _approved: boolean): void => {},
    onMetaInfoDeclareApprovalRequest: (_callback: unknown): (() => void) => noop,
    sendMetaInfoDeclareApprovalResponse: (_requestId: string, _approved: boolean): void => {},
    updateAppStatus: (_update: unknown): void => {},
  },

  autoUpdates: {
    getState: async () => ({
      status: 'disabled' as const,
      updateInfo: null,
      downloadProgress: null,
      errorMessage: null,
      currentVersion: null,
      arch: null,
      isDisabled: true,
      lastCheckAt: null,
      updateFallbackActive: false,
      installFailureDetected: false,
      macInstallIssue: null,
    }),
    check: async () => ({
      success: false,
      state: {
        status: 'disabled' as const,
        updateInfo: null,
        downloadProgress: null,
        errorMessage: 'Auto-updates are not available in browser mode.',
        currentVersion: null,
        arch: null,
        isDisabled: true,
        lastCheckAt: null,
        updateFallbackActive: false,
        installFailureDetected: false,
        macInstallIssue: null,
      },
      error: 'Auto-updates are not available in browser mode.',
    }),
    install: async () => ({
      success: false,
      error: 'Auto-updates are not available in browser mode.',
    }),
    onEvent: () => () => {},
    mock: {
      isEnabled: () => false,
      setScenario: async () => ({ success: false }),
      emitProgress: async () => ({ success: false }),
      emitDownloaded: async () => ({ success: false }),
      emitError: async () => ({ success: false }),
      reset: async () => ({ success: false }),
      getState: async () => ({
        scenario: { type: 'no-update' as const },
        downloadedUpdateInfo: null,
        isDownloading: false,
      }),
      getSchedulerState: async () => ({
        isRunning: false,
        isChecking: false,
        lastCheckAt: null,
        cooldownEndsAt: null,
        startupTimeoutId: null,
        intervalId: null,
      }),
      resetScheduler: async () => ({ success: false }),
    },
  },
  // Analytics API shim (no-op in web demo)
  analytics: {
    track: async () => ({ success: false }),
    pageview: async () => ({ success: false }),
    resourceSample: async () => ({ success: false }),
    setEnabled: async () => ({ success: false }),
    setContext: async () => ({ success: false }),
    onFlushChatCounts: () => {},
    getStartupTimings: async () => ({
      processCreationTimeMs: null,
      moduleLoadMs: 0,
      whenReadyMs: null,
      windowReadyToShowMs: null,
      didFinishLoadMs: null,
      safeStorageWarmMs: null,
    }),
    setUiContext: () => {},
  },
  // App settings API shim (no-op in web demo — no persistent app-settings.json)
  appSettings: {
    setDarkModeHint: async () => ({ success: true }),
    // No window to zoom in the browser demo: 100%, and the settings control
    // hides itself when `setUiScale` is missing — so this pair exists only to
    // satisfy the shared bridge type.
    getUiScale: async () => ({ scale: 1, platformDefault: 1, steps: [] }),
    setUiScale: async (scale: number) => ({ success: false, scale }),
  },
  // Sample database API shim (desktop-only; browser mode has its own demo-database picker)
  sampleDatabase: {
    provision: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },
  // Azure Data Studio import shim (no-op in web demo — no filesystem access)
  connectionImport: {
    list: async () => [],
    count: async () => [],
    scan: async (request: { sourceId: string }) => ({
      sourceId: request.sourceId as never,
      found: false as const,
      candidates: [],
    }),
    scanFile: async (request: { sourceId: string }) => ({
      sourceId: request.sourceId as never,
      found: false as const,
      candidates: [],
    }),
  },
  // Settings backup/transfer shim — the web demo has no native file dialogs.
  settingsTransfer: {
    exportBundle: async () => ({
      success: false as const,
      error: 'Not available in browser mode',
    }),
    importBundle: async () => ({
      success: false as const,
      error: 'Not available in browser mode',
    }),
  },
  // Connections export/import shim — the demo has no safeStorage and no
  // connections.json, so every call is an honest failure rather than a no-op.
  connectionTransfer: {
    exportConnections: async () => ({
      success: false as const,
      error: 'Not available in browser mode',
    }),
    previewImport: async () => ({
      success: false as const,
      error: 'Not available in browser mode',
    }),
    applyImport: async () => ({
      success: false as const,
      error: 'Not available in browser mode',
    }),
  },
  // Azure resource discovery shim (no-op in web demo — no az CLI / Entra identity)
  azureDiscovery: {
    resolveIdentity: async () => ({
      ok: false as const,
      error: { code: 'unknown' as const, message: 'Not available in browser mode' },
    }),
    list: async () => ({
      ok: false as const,
      error: { code: 'unknown' as const, message: 'Not available in browser mode' },
    }),
    cancel: async () => ({ ok: true as const, cancelled: false }),
    identity: async () => ({
      ok: false as const,
      error: { code: 'unknown' as const, message: 'Not available in browser mode' },
    }),
  },
  // Azure Storage shim (no-op in web demo — no storage SDKs in the browser);
  // every channel answers the typed `unsupported` StorageIpcResult.
  azureStorage: {
    testServices: azureStorageUnavailable,
    accountInfo: azureStorageUnavailable,
    list: azureStorageUnavailable,
    cancel: azureStorageUnavailable,
    cursorRelease: azureStorageUnavailable,
    getProperties: azureStorageUnavailable,
    download: azureStorageUnavailable,
    transferCancel: azureStorageUnavailable,
    transferResume: azureStorageUnavailable,
    transferRestart: azureStorageUnavailable,
    transferList: azureStorageUnavailable,
    transferClearFinished: azureStorageUnavailable,
    // S2 begin — kept inline with the standalone Electron preload block.
    upload: azureStorageUnavailable,
    delete: azureStorageUnavailable,
    createContainer: azureStorageUnavailable,
    deleteContainer: azureStorageUnavailable,
    recoverability: azureStorageUnavailable,
    transferDecide: azureStorageUnavailable,
    transferSettings: azureStorageUnavailable,
    onMutated: () => () => {},
    // S2 end
    // S5 begin — queue IPC
    queuePeek: azureStorageUnavailable,
    queueReceive: azureStorageUnavailable,
    queueSend: azureStorageUnavailable,
    queueUpdate: azureStorageUnavailable,
    queueMessageDelete: azureStorageUnavailable,
    queueClear: azureStorageUnavailable,
    queueCreate: azureStorageUnavailable,
    queueRemove: azureStorageUnavailable,
    // S5 end — queue IPC
    // S3 begin
    setMetadata: azureStorageUnavailable,
    setHeaders: azureStorageUnavailable,
    setTags: azureStorageUnavailable,
    setAccessTier: azureStorageUnavailable,
    getContainerAcl: azureStorageUnavailable,
    setContainerAcl: azureStorageUnavailable,
    generateSas: azureStorageUnavailable,
    getAccessPolicies: azureStorageUnavailable,
    setAccessPolicies: azureStorageUnavailable,
    onSasMinted: (_callback?: unknown) => () => {},
    onSasRequested: (_callback?: unknown) => () => {},
    // S3 end
    // S4 begin
    queryEntities: azureStorageUnavailable,
    getEntity: azureStorageUnavailable,
    writeEntities: azureStorageUnavailable,
    exportEntities: azureStorageUnavailable,
    createTable: azureStorageUnavailable,
    deleteTable: azureStorageUnavailable,
    keyAccessCheck: azureStorageUnavailable,
    // S4 end
    // S7 begin — file-share IPC
    shareCreate: azureStorageUnavailable,
    shareDelete: azureStorageUnavailable,
    shareProperties: azureStorageUnavailable,
    shareQuota: azureStorageUnavailable,
    shareSnapshots: azureStorageUnavailable,
    shareSnapshotCreate: azureStorageUnavailable,
    shareSnapshotDelete: azureStorageUnavailable,
    fileProperties: azureStorageUnavailable,
    directoryCreate: azureStorageUnavailable,
    fileUpload: azureStorageUnavailable,
    fileDownload: azureStorageUnavailable,
    fileDelete: azureStorageUnavailable,
    shareProtocol: azureStorageUnavailable,
    namedShares: azureStorageUnavailable,
    classifySharePath: azureStorageUnavailable,
    transferForget: azureStorageUnavailable,
    // S7 end — file-share IPC
    // S8 begin
    hnsResolvePath: azureStorageUnavailable,
    hnsCreateDirectory: azureStorageUnavailable,
    hnsMove: azureStorageUnavailable,
    hnsPendingMoves: azureStorageUnavailable,
    hnsGetAcl: azureStorageUnavailable,
    hnsSetAcl: azureStorageUnavailable,
    hnsAclRecursive: azureStorageUnavailable,
    // S8 end
    readContent: azureStorageUnavailable,
    writeContent: azureStorageUnavailable,
    onTransferProgress: () => () => {},
  },
  // Docker auto-discovery shim (no-op in web demo — no local Docker socket)
  dockerDiscovery: {
    availability: async () => ({ available: false, socketPath: '' }),
    detect: async () => ({ outcome: 'no_docker' as const, candidates: [] }),
  },
  // Crash Dialog API shim (no-op in web demo)
  crashDialog: {
    sendReport: async () => ({ success: false, reportId: '' }),
    setReportMode: async () => ({ success: false }),
    onMainProcessCrash: () => noop,
  },
  // AI Chat API shim (no-op in web demo)
  aiChat: {
    detectCli: async () => ({
      claude: { found: false, path: null },
      codex: { found: false, path: null },
    }),
    resetCliCache: async () => ({ success: true }),
    forceCliNotFound: async () => ({ success: true }),
    forceCodexNotFound: async () => ({ success: true }),
    forceClaudeNotFound: async () => ({ success: true }),
    sendMessage: async () => ({ success: false }),
    cancel: async () => ({ success: false }),
    loadHistory: async () => ({ success: false, messages: [], error: 'Not available in browser mode' }),
    getAvailableAdapters: async () => [],
    getPreferredAdapter: async () => null,
    setPreferredAdapter: async () => ({ success: true }),
    onStream: () => noop,
    onDone: () => noop,
    onError: () => noop,
  },
  // Script Browser API shim (no-op in web demo)
  scriptBrowser: {
    listDirectory: async () => ({ success: false, entries: [], error: 'Not available in browser mode' }),
    selectDirectory: async () => ({ cancelled: true }),
    scanDirectory: async () => ({ success: false, sqlFiles: [], error: 'Not available in browser mode' }),
  },
  // Database Blueprint API shim (no-op in web demo)
  databaseBlueprint: {
    setup: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    setupWithProgress: async () => ({
      success: false as const,
      error: 'Not available in browser mode',
    }),
    abortSetup: async () => ({ aborted: false }),
    onSetupProgress: () => () => {
      /* no-op in browser mode */
    },
    readMetadata: async () => ({
      success: false as const,
      error: 'Not available in browser mode',
      notFound: true,
    }),
    selectFolder: async () => ({ cancelled: true }),
    folderIsEmpty: async () => ({ empty: false, exists: false, error: 'Not available in browser mode' }),
    listSchemas: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    buildBundle: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    writeBundle: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    discardBundle: async () => ({ success: true as const, discarded: false }),
    onBundleWriteProgress: () => () => {
      /* no-op in browser mode */
    },
    detectDrift: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    threeWayMerge: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    onThreeWayMergeProgress: () => () => {
      /* no-op in browser mode */
    },
    addLink: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    removeLink: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    setActiveLink: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    exportMeta: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    readMeta: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    autoImportMeta: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    gitStatus: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    gitInit: async () => ({ success: false, error: 'Not available in browser mode' }),
    gitCommit: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    gitBranches: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    gitCheckout: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    gitPull: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    gitPush: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    gitMarkResolved: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    writeFile: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    writeBaselineFile: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    statFile: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    setExclusions: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listBlueprintObjects: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listBlueprintDataTables: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    detectMissingSidecars: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    fkOrderDataTables: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    rebuildDataCache: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    readSidecarPk: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    readSidecar: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    applyTableEdits: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    listSourceObjects: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    countRows: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    writeBlueprintData: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    updateSettings: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    applyDestructiveReplay: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    folderObjectCount: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    registryList: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    registryAdd: async () => ({ success: false as const, error: 'Not available in browser mode' }),
    registryRemove: async () => ({ success: false as const, error: 'Not available in browser mode' }),
  },
  // Two-phase test-connection shim (not available in browser/web demo mode)
  testConnectionStepwise: async () => ({
    ssh: { status: 'skipped' as const, detail: 'web demo' },
    db: { status: 'error' as const, detail: 'Not available in web demo' },
  }),
  // SSH config inspection shim (not available in browser/web demo mode)
  ssh: {
    readConfigAliases: async () => [],
    fileExists: async (_path: string) => false,
    openKeyFile: async () => undefined,
    forgetHost: async (_host: string, _port: number) => undefined,
    onTunnelStateChange: (_listener: (evt: { connectionId: string; state: 'connecting' | 'up' | 'down' | 'error' }) => void) => () => undefined,
  },
  // Generic invoke shim (no-op in web demo)
  invoke: async () => ({ success: false }),
};

// Install the shim on window.electronAPI
if (typeof window !== 'undefined') {
  // Only install if not already present (e.g., in actual Electron environment)
  if (!window.electronAPI) {
    window.electronAPI = electronApiShim;
    console.log('[WebDemo] ElectronAPI shim installed');
  }
}

export default electronApiShim;
